import { useParentMenuGroups } from '../hooks/useMenuGroups';
import { useSelectedLocation } from '../contexts/SelectionContext';
import { IMenuGroup } from '@order-up/models';
import React from 'react';

interface MenuSelectionWizardProps {
    onMenuGroupSelect: (menuGroup: IMenuGroup | null) => void;
    /** Custom styling */
    className?: string;
    /** Show "View All Menus" option */
    showAllMenusOption?: boolean;
    /** Custom loading message */
    loadingMessage?: string;
    /** Custom error message */
    errorMessage?: string;
    /** Custom no menus message */
    noMenusMessage?: string;
}

export const MenuSelectionWizard: React.FC<MenuSelectionWizardProps> = ({
    onMenuGroupSelect,
    className = '',
    showAllMenusOption = true,
    loadingMessage = 'Loading available menus...',
    errorMessage = 'Unable to load menus for this restaurant.',
    noMenusMessage = 'No menus available'
}) => {
    const { restaurant: locationRecord } = useSelectedLocation();
    const { data: parentMenuGroups = [], isLoading, error } = useParentMenuGroups();

    // Loading state — the selection screen's own frame, which is what this
    // wait usually resolves to. The static description and each card's CTA
    // print for real; the restaurant's name (when the context has not got it
    // yet) and the menu names and descriptions are held.
    if (isLoading) {
        return (
            <div
                className={`menu-selection-wizard ${className}`}
                role="status"
                aria-busy
                aria-label={loadingMessage}
            >
                <div className="menu-selection-wizard__container">
                    <div className="menu-selection-wizard__header">
                        {locationRecord?.name ? (
                            <h1 className="menu-selection-wizard__title">
                                Welcome to {locationRecord.name}
                            </h1>
                        ) : (
                            <span aria-hidden className="menu-selection-wizard__bar menu-selection-wizard__bar--heading" />
                        )}
                        <p className="menu-selection-wizard__description">
                            Choose which menu you'd like to view
                        </p>
                    </div>

                    <div className="menu-selection-wizard__grid" aria-hidden>
                        {[0, 1, 2].map((i) => (
                            <div
                                key={i}
                                className="menu-selection-wizard__card menu-selection-wizard__card--pending"
                            >
                                <span className="menu-selection-wizard__bar menu-selection-wizard__bar--title" />
                                <span className="menu-selection-wizard__bar menu-selection-wizard__bar--line" />
                                <span className="menu-selection-wizard__bar menu-selection-wizard__bar--line menu-selection-wizard__bar--short" />
                                <div className="menu-selection-wizard__card-footer">
                                    <span className="menu-selection-wizard__card-cta">
                                        View Menu →
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className={`menu-selection-wizard__error ${className}`}>
                <div className="menu-selection-wizard__error-content">
                    <h1 className="menu-selection-wizard__error-title">Error Loading Menus</h1>
                    <p className="menu-selection-wizard__error-message">{errorMessage}</p>
                </div>
            </div>
        );
    }

    // Auto-select if only one menu and website config specifies it
    const websiteParentMenuGroupId = locationRecord?.websiteConfig?.parentMenuGroupId;
    if (websiteParentMenuGroupId) {
        const specifiedMenuGroup = parentMenuGroups.find(
            group => group.menuGroupId === websiteParentMenuGroupId
        );
        if (specifiedMenuGroup) {
            // Auto-navigate to the specified menu
            onMenuGroupSelect(specifiedMenuGroup);
            return (
                <div className={`menu-selection-wizard__loading ${className}`}>
                    <div className="menu-selection-wizard__loading-content">
                        <p className="menu-selection-wizard__loading-text">Loading menu...</p>
                    </div>
                </div>
            );
        }
    }

    // Auto-select if only one menu
    if (parentMenuGroups.length === 1) {
        // Auto-navigate to the single menu
        onMenuGroupSelect(parentMenuGroups[0]);
        return (
            <div className={`menu-selection-wizard__loading ${className}`}>
                <div className="menu-selection-wizard__loading-content">
                    <p className="menu-selection-wizard__loading-text">Loading menu...</p>
                </div>
            </div>
        );
    }

    // No menus available
    if (parentMenuGroups.length === 0) {
        return (
            <div className={`menu-selection-wizard__error ${className}`}>
                <div className="menu-selection-wizard__error-content">
                    <h1 className="menu-selection-wizard__error-title">No Menus Available</h1>
                    <p className="menu-selection-wizard__error-message">
                        {locationRecord?.name || 'This restaurant'} doesn't have any menus configured yet.
                    </p>
                    <p className="menu-selection-wizard__error-message">
                        {noMenusMessage}
                    </p>
                </div>
            </div>
        );
    }

    // Multiple menus - show selection
    return (
        <div className={`menu-selection-wizard ${className}`}>
            <div className="menu-selection-wizard__container">
                <div className="menu-selection-wizard__header">
                    <h1 className="menu-selection-wizard__title">
                        Welcome to {locationRecord?.name || 'Our Restaurant'}
                    </h1>
                    <p className="menu-selection-wizard__description">
                        Choose which menu you'd like to view
                    </p>
                </div>

                <div className="menu-selection-wizard__grid">
                    {parentMenuGroups.map((menuGroup) => (
                        <button
                            key={menuGroup.menuGroupId || menuGroup.guid}
                            onClick={() => onMenuGroupSelect(menuGroup)}
                            className="menu-selection-wizard__card"
                        >
                            <div className="menu-selection-wizard__card-content">
                                <h3 className="menu-selection-wizard__card-title">
                                    {menuGroup.name}
                                </h3>

                                {menuGroup.description && (
                                    <p className="menu-selection-wizard__card-description">
                                        {menuGroup.description}
                                    </p>
                                )}

                                <div className="menu-selection-wizard__card-footer">
                                    <span className="menu-selection-wizard__card-cta">
                                        View Menu →
                                    </span>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>

                {/* Option to view all menus combined */}
                {showAllMenusOption && (
                    <div className="menu-selection-wizard__all-menus">
                        <button
                            onClick={() => onMenuGroupSelect(null)} // null represents "all menus"
                            className="menu-selection-wizard__all-menus-button"
                        >
                            View All Menus
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
